import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { aeClinicalAPI, aeAPI } from '../../utils/api';
import { toast } from 'react-toastify';

const ROUTES = ['IV', 'IM', 'SC', 'Oral', 'SL', 'Inhaled', 'Topical', 'PR', 'Nasal'];
const COMMON_DRUGS = ['Morphine', 'Paracetamol', 'Metoclopramide', 'Ondansetron', 'Diazepam', 'Midazolam', 'Adrenaline', 'Atropine', 'Salbutamol', 'Hydrocortisone', 'Dexamethasone', 'Ceftriaxone', 'Amoxicillin', 'Metronidazole', 'Aspirin', 'GTN Spray', 'Enoxaparin', 'Heparin', 'Furosemide', 'Other'];
const FLUIDS = ['Normal Saline 0.9%', 'Dextrose 5%', 'Dextrose Saline', 'Ringer Lactate', 'Hartmann Solution', 'Albumin 20%', 'Blood (Whole)', 'Packed Red Cells', 'FFP', 'Platelets', 'Dextrose 10%', 'Sodium Bicarbonate 8.4%', 'Gelofusine', 'Mannitol 20%'];

const BODY_REGIONS = [
    { id: 'head', label: 'Head & Skull' }, { id: 'face', label: 'Face' }, { id: 'neck', label: 'Neck' },
    { id: 'chest_l', label: 'Chest (Left)' }, { id: 'chest_r', label: 'Chest (Right)' },
    { id: 'abdomen', label: 'Abdomen' }, { id: 'pelvis', label: 'Pelvis / Groin' },
    { id: 'spine', label: 'Spine / Back' }, { id: 'shoulder_l', label: 'Shoulder (L)' }, { id: 'shoulder_r', label: 'Shoulder (R)' },
    { id: 'arm_l', label: 'Arm (Left)' }, { id: 'arm_r', label: 'Arm (Right)' },
    { id: 'forearm_l', label: 'Forearm (Left)' }, { id: 'forearm_r', label: 'Forearm (Right)' },
    { id: 'hand_l', label: 'Hand (Left)' }, { id: 'hand_r', label: 'Hand (Right)' },
    { id: 'thigh_l', label: 'Thigh (Left)' }, { id: 'thigh_r', label: 'Thigh (Right)' },
    { id: 'leg_l', label: 'Leg (Left)' }, { id: 'leg_r', label: 'Leg (Right)' },
    { id: 'foot_l', label: 'Foot (Left)' }, { id: 'foot_r', label: 'Foot (Right)' },
    { id: 'genitalia', label: 'Genitalia' }, { id: 'buttocks', label: 'Buttocks' },
];

const INJURY_TYPES = ['Laceration', 'Contusion / Bruise', 'Abrasion', 'Fracture', 'Dislocation', 'Burns', 'Gunshot Wound', 'Stab Wound', 'Amputation', 'Swelling / Haematoma', 'Internal Injury'];

const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' };

export default function AEClinical() {
    const { visit_id } = useParams();
    const navigate = useNavigate();
    const [tab, setTab] = useState('mar');

    const [mar, setMar] = useState([]);
    const [marForm, setMarForm] = useState({ drug_name: '', dose: '', route: 'IV', notes: '' });
    const [marLoading, setMarLoading] = useState(false);
    const [marSaving, setMarSaving] = useState(false);

    const [fluids, setFluids] = useState([]);
    const [fluidForm, setFluidForm] = useState({ fluid_type: '', volume_ml: '', rate_ml_hr: '', notes: '' });
    const [fluidLoading, setFluidLoading] = useState(false);
    const [fluidSaving, setFluidSaving] = useState(false);

    // MLC body diagram state
    const [injuredRegions, setInjuredRegions] = useState({});
    const [injuryDesc, setInjuryDesc] = useState('');
    const [mlcSaving, setMlcSaving] = useState(false);

    // Court summons state
    const [summons, setSummons] = useState([]);
    const [summonsForm, setSummonsForm] = useState({ court_name: '', case_number: '', date_received: '', hearing_date: '', notes: '' });
    const [summonsSaving, setSummonsSaving] = useState(false);

    const loadMAR = async () => {
        setMarLoading(true);
        try { const res = await aeClinicalAPI.getMAR(visit_id); setMar(res.data.mar); }
        catch { toast.error('Failed to load MAR'); } finally { setMarLoading(false); }
    };

    const loadFluids = async () => {
        setFluidLoading(true);
        try { const res = await aeClinicalAPI.getFluids(visit_id); setFluids(res.data.fluids); }
        catch { toast.error('Failed to load IV fluids'); } finally { setFluidLoading(false); }
    };

    const loadSummons = async () => {
        try {
            const res = await aeClinicalAPI.getSummons(visit_id);
            setSummons(res.data.summons || []);
        } catch { setSummons([]); }
    };

    const loadMLCDiagram = async () => {
        try {
            const res = await aeClinicalAPI.getMLCDiagram(visit_id);
            if (res.data?.diagram) {
                const d = res.data.diagram;
                const regions = {};
                (d.injured_regions || []).forEach(r => { regions[r] = true; });
                setInjuredRegions(regions);
                setInjuryDesc(d.injury_description || '');
            }
        } catch { }
    };

    useEffect(() => { loadMAR(); loadFluids(); loadSummons(); loadMLCDiagram(); }, [visit_id]);

    const addMAR = async () => {
        if (!marForm.drug_name) return toast.error('Select a drug');
        setMarSaving(true);
        try {
            await aeClinicalAPI.addMAR({ visit_id, ...marForm });
            toast.success('Drug recorded');
            setMarForm({ drug_name: '', dose: '', route: 'IV', notes: '' });
            loadMAR();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to save'); }
        finally { setMarSaving(false); }
    };

    const deleteMAR = async (id) => {
        if (!window.confirm('Delete this record?')) return;
        try { await aeClinicalAPI.deleteMAR(id); setMar(m => m.filter(r => r.id !== id)); }
        catch { toast.error('Delete failed'); }
    };

    const addFluid = async () => {
        if (!fluidForm.fluid_type) return toast.error('Select a fluid');
        setFluidSaving(true);
        try {
            await aeClinicalAPI.addFluid({ visit_id, ...fluidForm });
            toast.success('IV fluid started');
            setFluidForm({ fluid_type: '', volume_ml: '', rate_ml_hr: '', notes: '' });
            loadFluids();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to save'); }
        finally { setFluidSaving(false); }
    };

    const stopFluid = async (id) => {
        try { await aeClinicalAPI.stopFluid(id); toast.success('Marked as completed'); loadFluids(); }
        catch { toast.error('Failed to update'); }
    };

    const deleteFluid = async (id) => {
        if (!window.confirm('Delete this record?')) return;
        try { await aeClinicalAPI.deleteFluid(id); setFluids(f => f.filter(r => r.id !== id)); }
        catch { toast.error('Delete failed'); }
    };

    const toggleRegion = (id) => {
        setInjuredRegions(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const saveMLCDiagram = async () => {
        setMlcSaving(true);
        try {
            const regions = Object.keys(injuredRegions).filter(k => injuredRegions[k]);
            await aeClinicalAPI.saveMLCDiagram({ visit_id, injured_regions: regions, injury_description: injuryDesc });
            toast.success('MLC injury diagram saved');
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to save diagram'); }
        finally { setMlcSaving(false); }
    };

    const printMLCDiagram = () => {
        const regions = Object.keys(injuredRegions).filter(k => injuredRegions[k]);
        const regionLabels = regions.map(r => BODY_REGIONS.find(b => b.id === r)?.label || r).join(', ');
        const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
            body{font-family:Arial,sans-serif;padding:24px;max-width:800px;margin:0 auto;}
            h2{text-align:center;color:#1e3a5f;border-bottom:2px solid #1e3a5f;padding-bottom:8px;}
            .badge{display:inline-block;background:#dc2626;color:white;padding:3px 10px;border-radius:4px;font-size:12px;font-weight:700;}
            .grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin:16px 0;}
            .region{padding:8px;border:1px solid #e2e8f0;border-radius:6px;font-size:13px;}
            .region.injured{background:#fef2f2;border-color:#dc2626;font-weight:700;color:#dc2626;}
            .sig{display:grid;grid-template-columns:1fr 1fr 1fr;gap:30px;margin-top:40px;}
            .sig-line{border-top:1px solid #374151;padding-top:6px;text-align:center;font-size:11px;}
            @media print{@page{size:A4;margin:12mm}}
        </style></head><body>
        <h2>🏥 HIMS — MLC Injury Documentation<br><span style="font-size:14px;">Visit #${visit_id}</span></h2>
        <p><span class="badge">MEDICO-LEGAL DOCUMENT</span> &nbsp; Printed: ${new Date().toLocaleString('en-PK')}</p>
        <h3 style="margin-top:16px;">Injured Body Regions</h3>
        <div class="grid">
            ${BODY_REGIONS.map(r => `<div class="region ${injuredRegions[r.id] ? 'injured' : ''}">${injuredRegions[r.id] ? '⚫' : '○'} ${r.label}</div>`).join('')}
        </div>
        <h3>Injury Description</h3>
        <div style="border:1px solid #e2e8f0;padding:12px;border-radius:6px;min-height:80px;">${injuryDesc || '—'}</div>
        <div class="sig">
            <div><div class="sig-line">Examining Doctor</div></div>
            <div><div class="sig-line">MO / Duty Doctor</div></div>
            <div><div class="sig-line">Police Officer</div></div>
        </div>
        </body></html>`;
        const w = window.open('', '_blank', 'width=800,height=900');
        if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 600); }
    };

    const addSummons = async () => {
        if (!summonsForm.court_name) return toast.error('Court name required');
        setSummonsSaving(true);
        try {
            await aeClinicalAPI.addSummons({ visit_id: parseInt(visit_id), ...summonsForm });
            toast.success('Court summons recorded');
            setSummonsForm({ court_name: '', case_number: '', date_received: '', hearing_date: '', notes: '' });
            loadSummons();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to save'); }
        finally { setSummonsSaving(false); }
    };

    const fmtTime = (t) => t ? new Date(t).toLocaleString('en-PK', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short', year: 'numeric' }) : '—';
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

    return (
        <div style={{ maxWidth: 960 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <button onClick={() => navigate('/ae')} style={{ padding: '6px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
                    ← Back to ER
                </button>
                <span style={{ fontSize: 28 }}>💊</span>
                <div>
                    <h2 style={{ fontSize: 18, fontWeight: 700, color: '#991b1b', margin: 0 }}>Clinical Record — Visit #{visit_id}</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Medication Administration Record, IV Fluids, MLC Diagram & Court Summons</p>
                </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #e2e8f0' }}>
                {[
                    { key: 'mar', label: '💊 MAR', count: mar.length },
                    { key: 'fluid', label: '💧 IV Fluids', count: fluids.length },
                    { key: 'mlc', label: '⚖️ MLC Diagram', count: null },
                    { key: 'summons', label: '📋 Court Summons', count: summons.length },
                ].map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)}
                        style={{ padding: '10px 20px', border: 'none', borderBottom: tab === t.key ? '3px solid #dc2626' : '3px solid transparent',
                            background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: tab === t.key ? 700 : 400,
                            color: tab === t.key ? '#dc2626' : '#64748b', whiteSpace: 'nowrap' }}>
                        {t.label} {t.count > 0 && <span style={{ background: '#dc2626', color: 'white', borderRadius: 10, padding: '1px 7px', fontSize: 11, marginLeft: 4 }}>{t.count}</span>}
                    </button>
                ))}
            </div>

            {/* MAR Tab */}
            {tab === 'mar' && (
                <div>
                    <div style={{ background: '#fff5f5', border: '1px solid #fecaca', borderRadius: 10, padding: 20, marginBottom: 20 }}>
                        <h3 style={{ fontSize: 13, fontWeight: 700, color: '#991b1b', marginBottom: 14 }}>Record Drug Administration</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Drug *</label>
                                <select style={{ ...inputStyle, background: 'white' }} value={marForm.drug_name} onChange={e => setMarForm(f => ({ ...f, drug_name: e.target.value }))}>
                                    <option value="">Select drug</option>
                                    {COMMON_DRUGS.map(d => <option key={d}>{d}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Dose</label>
                                <input style={inputStyle} value={marForm.dose} onChange={e => setMarForm(f => ({ ...f, dose: e.target.value }))} placeholder="e.g. 500mg" />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Route</label>
                                <select style={{ ...inputStyle, background: 'white' }} value={marForm.route} onChange={e => setMarForm(f => ({ ...f, route: e.target.value }))}>
                                    {ROUTES.map(r => <option key={r}>{r}</option>)}
                                </select>
                            </div>
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Notes</label>
                            <input style={inputStyle} value={marForm.notes} onChange={e => setMarForm(f => ({ ...f, notes: e.target.value }))} placeholder="Additional notes (optional)" />
                        </div>
                        <button onClick={addMAR} disabled={marSaving}
                            style={{ padding: '10px 24px', background: marSaving ? '#9ca3af' : '#dc2626', color: 'white', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                            {marSaving ? 'Saving...' : '+ Record Administration'}
                        </button>
                    </div>
                    <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                            <span style={{ fontWeight: 700, fontSize: 13, color: '#1e3a5f' }}>Medication Record ({mar.length} entries)</span>
                        </div>
                        {marLoading ? <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading...</div>
                        : mar.length === 0 ? <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>No medications recorded yet</div>
                        : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead><tr style={{ background: '#f8fafc' }}>
                                    {['Time', 'Drug', 'Dose', 'Route', 'Given By', 'Notes', ''].map(h => (
                                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                    ))}
                                </tr></thead>
                                <tbody>
                                    {mar.map(r => (
                                        <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '10px 14px', fontSize: 12, whiteSpace: 'nowrap' }}>{fmtTime(r.administered_at)}</td>
                                            <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 700 }}>{r.drug_name}</td>
                                            <td style={{ padding: '10px 14px', fontSize: 12 }}>{r.dose || '—'}</td>
                                            <td style={{ padding: '10px 14px' }}><span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{r.route || '—'}</span></td>
                                            <td style={{ padding: '10px 14px', fontSize: 12 }}>{r.administered_by || '—'}</td>
                                            <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{r.notes || '—'}</td>
                                            <td style={{ padding: '10px 14px' }}>
                                                <button onClick={() => deleteMAR(r.id)} style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}>🗑</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {/* IV Fluid Tab */}
            {tab === 'fluid' && (
                <div>
                    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 20, marginBottom: 20 }}>
                        <h3 style={{ fontSize: 13, fontWeight: 700, color: '#1e40af', marginBottom: 14 }}>Start IV Fluid</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Fluid Type *</label>
                                <select style={{ ...inputStyle, background: 'white' }} value={fluidForm.fluid_type} onChange={e => setFluidForm(f => ({ ...f, fluid_type: e.target.value }))}>
                                    <option value="">Select fluid</option>
                                    {FLUIDS.map(fl => <option key={fl}>{fl}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Volume (ml)</label>
                                <input type="number" style={inputStyle} value={fluidForm.volume_ml} onChange={e => setFluidForm(f => ({ ...f, volume_ml: e.target.value }))} placeholder="500" />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Rate (ml/hr)</label>
                                <input type="number" style={inputStyle} value={fluidForm.rate_ml_hr} onChange={e => setFluidForm(f => ({ ...f, rate_ml_hr: e.target.value }))} placeholder="125" />
                            </div>
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Notes</label>
                            <input style={inputStyle} value={fluidForm.notes} onChange={e => setFluidForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. via 18G cannula, right antecubital" />
                        </div>
                        <button onClick={addFluid} disabled={fluidSaving}
                            style={{ padding: '10px 24px', background: fluidSaving ? '#9ca3af' : '#1d4ed8', color: 'white', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                            {fluidSaving ? 'Saving...' : '+ Start IV Fluid'}
                        </button>
                    </div>
                    <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                            <span style={{ fontWeight: 700, fontSize: 13, color: '#1e3a5f' }}>IV Fluid Chart ({fluids.length} entries)</span>
                        </div>
                        {fluidLoading ? <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading...</div>
                        : fluids.length === 0 ? <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>No IV fluids recorded yet</div>
                        : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead><tr style={{ background: '#f8fafc' }}>
                                    {['Start Time', 'Fluid', 'Volume', 'Rate', 'Status', 'End Time', 'Notes', ''].map(h => (
                                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                    ))}
                                </tr></thead>
                                <tbody>
                                    {fluids.map(f => (
                                        <tr key={f.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '10px 14px', fontSize: 12, whiteSpace: 'nowrap' }}>{fmtTime(f.start_time)}</td>
                                            <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 700 }}>{f.fluid_type}</td>
                                            <td style={{ padding: '10px 14px', fontSize: 12 }}>{f.volume_ml ? `${f.volume_ml} ml` : '—'}</td>
                                            <td style={{ padding: '10px 14px', fontSize: 12 }}>{f.rate_ml_hr ? `${f.rate_ml_hr} ml/hr` : '—'}</td>
                                            <td style={{ padding: '10px 14px' }}>
                                                <span style={{ background: f.status === 'Running' ? '#dcfce7' : '#f1f5f9', color: f.status === 'Running' ? '#16a34a' : '#64748b', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
                                                    {f.status === 'Running' ? '🟢 Running' : '✅ Done'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '10px 14px', fontSize: 12 }}>{fmtTime(f.end_time)}</td>
                                            <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{f.notes || '—'}</td>
                                            <td style={{ padding: '10px 14px', display: 'flex', gap: 6 }}>
                                                {f.status === 'Running' && (
                                                    <button onClick={() => stopFluid(f.id)}
                                                        style={{ padding: '3px 8px', background: '#059669', color: 'white', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>Stop</button>
                                                )}
                                                <button onClick={() => deleteFluid(f.id)} style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}>🗑</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {/* MLC Body Diagram Tab */}
            {tab === 'mlc' && (
                <div>
                    <div style={{ background: '#fef2f2', border: '2px solid #dc2626', borderRadius: 10, padding: 20, marginBottom: 20 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#991b1b', margin: 0 }}>⚖️ MLC Injury Body Diagram</h3>
                            <span style={{ background: '#dc2626', color: 'white', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 4 }}>MEDICO-LEGAL</span>
                        </div>
                        <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>Click on injured body regions to mark them. Red = Injured.</p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
                            {BODY_REGIONS.map(r => (
                                <button key={r.id} onClick={() => toggleRegion(r.id)}
                                    style={{ padding: '9px 10px', border: `2px solid ${injuredRegions[r.id] ? '#dc2626' : '#e2e8f0'}`, borderRadius: 7, background: injuredRegions[r.id] ? '#fef2f2' : 'white', color: injuredRegions[r.id] ? '#dc2626' : '#374151', fontWeight: injuredRegions[r.id] ? 700 : 400, cursor: 'pointer', fontSize: 12, textAlign: 'left' }}>
                                    {injuredRegions[r.id] ? '⚫' : '○'} {r.label}
                                </button>
                            ))}
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 6, color: '#374151' }}>Detailed Injury Description</label>
                            <textarea value={injuryDesc} onChange={e => setInjuryDesc(e.target.value)}
                                placeholder="Describe injuries in detail — size, depth, location, characteristics..."
                                style={{ ...inputStyle, height: 90, resize: 'vertical' }} />
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={saveMLCDiagram} disabled={mlcSaving}
                                style={{ padding: '10px 20px', background: mlcSaving ? '#9ca3af' : '#dc2626', color: 'white', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                                {mlcSaving ? 'Saving...' : '💾 Save Injury Diagram'}
                            </button>
                            <button onClick={printMLCDiagram}
                                style={{ padding: '10px 20px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                                🖨 Print MLC Injury Form
                            </button>
                        </div>
                    </div>
                    <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', padding: 16 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', marginBottom: 10 }}>Currently Marked Regions</div>
                        {Object.keys(injuredRegions).filter(k => injuredRegions[k]).length === 0 ? (
                            <p style={{ color: '#94a3b8', fontSize: 13 }}>No regions marked yet</p>
                        ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {Object.keys(injuredRegions).filter(k => injuredRegions[k]).map(k => (
                                    <span key={k} style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                                        {BODY_REGIONS.find(r => r.id === k)?.label || k}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Court Summons Tab */}
            {tab === 'summons' && (
                <div>
                    <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: 20, marginBottom: 20 }}>
                        <h3 style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 14 }}>📋 Record Court Summons</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Court Name *</label>
                                <input style={inputStyle} value={summonsForm.court_name} onChange={e => setSummonsForm(f => ({ ...f, court_name: e.target.value }))} placeholder="e.g. Sessions Court Karachi" />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Case / FIR Number</label>
                                <input style={inputStyle} value={summonsForm.case_number} onChange={e => setSummonsForm(f => ({ ...f, case_number: e.target.value }))} placeholder="e.g. FIR-2024-1234" />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Date Received</label>
                                <input type="date" style={inputStyle} value={summonsForm.date_received} onChange={e => setSummonsForm(f => ({ ...f, date_received: e.target.value }))} />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Hearing Date</label>
                                <input type="date" style={inputStyle} value={summonsForm.hearing_date} onChange={e => setSummonsForm(f => ({ ...f, hearing_date: e.target.value }))} />
                            </div>
                            <div style={{ gridColumn: 'span 2' }}>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Notes</label>
                                <input style={inputStyle} value={summonsForm.notes} onChange={e => setSummonsForm(f => ({ ...f, notes: e.target.value }))} placeholder="Additional notes" />
                            </div>
                        </div>
                        <button onClick={addSummons} disabled={summonsSaving}
                            style={{ padding: '10px 24px', background: summonsSaving ? '#9ca3af' : '#d97706', color: 'white', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                            {summonsSaving ? 'Saving...' : '+ Add Summons Record'}
                        </button>
                    </div>
                    <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                            <span style={{ fontWeight: 700, fontSize: 13, color: '#1e3a5f' }}>Court Summons Record ({summons.length} entries)</span>
                        </div>
                        {summons.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>No court summons recorded</div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead><tr style={{ background: '#f8fafc' }}>
                                    {['Court', 'Case / FIR', 'Date Received', 'Hearing Date', 'Notes'].map(h => (
                                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                    ))}
                                </tr></thead>
                                <tbody>
                                    {summons.map((s, i) => (
                                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 700 }}>{s.court_name}</td>
                                            <td style={{ padding: '10px 14px', fontSize: 12 }}>{s.case_number || '—'}</td>
                                            <td style={{ padding: '10px 14px', fontSize: 12 }}>{fmtDate(s.date_received)}</td>
                                            <td style={{ padding: '10px 14px', fontSize: 12 }}>
                                                <span style={{ color: new Date(s.hearing_date) < new Date() ? '#dc2626' : '#059669', fontWeight: 600 }}>
                                                    {fmtDate(s.hearing_date)}
                                                </span>
                                            </td>
                                            <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{s.notes || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
